export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
  articleCount: number;
}

export interface Article {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  imageSeq: string;
  imageQuery: string;
}

export const categories: Category[] = [
  {
    slug: 'tutorial',
    name: '使用教程',
    description: '从零开始学习如何使用导航页在线发布平台，涵盖基础操作到高级技巧。',
    icon: 'ri-book-open-line',
    articleCount: 12,
  },
  {
    slug: 'updates',
    name: '更新日志',
    description: '了解导航页在线发布平台的最新功能更新、版本迭代和改进内容。',
    icon: 'ri-refresh-line',
    articleCount: 8,
  },
  {
    slug: 'help',
    name: '帮助中心',
    description: '常见操作问题和解决方案，帮你快速解决使用过程中遇到的各类问题。',
    icon: 'ri-question-answer-line',
    articleCount: 15,
  },
  {
    slug: 'cases',
    name: '用户案例',
    description: '看看其他用户如何利用导航页在线发布平台提升工作效率和团队协作。',
    icon: 'ri-star-line',
    articleCount: 6,
  },
];

export const articles: Article[] = [
  {
    slug: 'getting-started',
    title: '新手指南：5分钟创建你的第一个导航页',
    category: 'tutorial',
    excerpt: '本指南将带你从注册账号到发布第一个导航页，全程只需5分钟，无需任何技术背景。',
    content: `<h2>第一步：注册账号</h2><p>访问导航页在线发布官网，点击右上角"免费开始"按钮。你可以使用邮箱或手机号注册，输入验证码后即可完成账号创建。整个过程只需30秒。</p><h2>第二步：创建导航页</h2><p>登录后进入工作台，点击"新建导航页"按钮。输入导航页名称（如"我的常用工具"），系统会自动为你创建一个空白导航页。</p><h2>第三步：添加分类</h2><p>点击"添加分类"按钮，创建你的第一个分类。常见的分类有"开发工具"、"设计资源"、"学习平台"等。你可以通过拖拽调整分类顺序。</p><h2>第四步：添加链接</h2><p>在每个分类下，点击"添加链接"，输入网站名称、网址和描述。系统会自动抓取网站图标，你也可以手动上传。支持批量导入浏览器书签，非常方便。</p><h2>第五步：选择主题</h2><p>在"主题设置"中选择你喜欢的模板和配色。我们提供多种精心设计的主题，从简约到炫酷，总有一款适合你。</p><h2>第六步：发布分享</h2><p>预览确认无误后，点击"发布"按钮。系统会生成一个专属链接，你可以直接复制分享给团队或用户。专业版用户还可以绑定自定义域名。</p>`,
    author: '导航页团队',
    date: '2026-07-28',
    readTime: '5分钟',
    tags: ['入门', '教程', '新手指南'],
    imageSeq: 'article-getting-started',
    imageQuery: 'Modern%20workspace%20with%20laptop%20showing%20a%20clean%20navigation%20page%20interface%2C%20soft%20natural%20lighting%2C%20minimalist%20desk%20setup%2C%20warm%20wooden%20textures%2C%20professional%20editorial%20photography%2C%20high%20detail',
  },
  {
    slug: 'custom-domain',
    title: '如何绑定自定义域名到你的导航页',
    category: 'tutorial',
    excerpt: '详细教程：手把手教你将导航页绑定到自己的域名，提升品牌专业度和用户信任感。',
    content: `<h2>为什么要绑定自定义域名？</h2><p>使用自定义域名可以让你的导航页看起来更加专业。比如用 nav.yourcompany.com 代替平台默认的链接，增强品牌认知度和用户信任。</p><h2>前提条件</h2><p>绑定自定义域名需要满足两个条件：第一，你已升级到专业版或企业版；第二，你拥有一个域名并可以管理其 DNS 记录。</p><h2>操作步骤</h2><h3>1. 进入域名设置</h3><p>在工作台左侧菜单找到"域名设置"，点击进入。选择"绑定自定义域名"。</p><h3>2. 输入域名</h3><p>输入你想绑定的域名，例如 nav.yourdomain.com。系统会验证域名格式是否正确。</p><h3>3. 配置DNS记录</h3><p>系统会生成一条 CNAME 记录，你需要去域名服务商（如阿里云、腾讯云）的DNS管理页面添加这条记录。具体操作是：添加一条 CNAME 类型的记录，主机记录填写你设置的子域名（如 nav），记录值填写系统提供的目标地址。</p><h3>4. 等待生效</h3><p>DNS 记录添加后通常需要几分钟到几小时生效。你可以在页面中点击"验证"按钮检查是否配置成功。验证通过后，你的导航页就可以通过自定义域名访问了。</p>`,
    author: '导航页团队',
    date: '2026-07-22',
    readTime: '8分钟',
    tags: ['域名', '高级功能', '教程'],
    imageSeq: 'article-domain',
    imageQuery: 'Web%20domain%20configuration%20concept%20with%20clean%20minimalist%20interface%20showing%20DNS%20settings%2C%20professional%20tech%20aesthetic%2C%20soft%20blue%20and%20white%20color%20palette%2C%20modern%20UI%20design%2C%20editorial%20photography',
  },
  {
    slug: 'team-collaboration',
    title: '团队协作功能详解：多人共同管理导航页',
    category: 'tutorial',
    excerpt: '了解如何使用团队协作功能，让多位成员共同编辑和管理同一个导航页，提升团队效率。',
    content: `<h2>团队协作概述</h2><p>企业版支持团队协作功能，允许多位成员共同编辑和管理导航页。每个成员可以拥有不同的权限级别，确保内容安全。</p><h2>邀请团队成员</h2><p>在导航页设置中，点击"团队成员"标签。输入成员的邮箱地址，选择权限级别（管理员、编辑者、查看者），发送邀请即可。</p><h2>权限级别说明</h2><p><strong>管理员：</strong>拥有完全控制权，可以编辑内容、管理成员、修改设置。<br><strong>编辑者：</strong>可以添加和编辑链接、分类，但不能管理成员和修改设置。<br><strong>查看者：</strong>只能查看导航页内容和数据，不能做任何修改。</p><h2>协作注意事项</h2><p>多人同时编辑时，系统会自动保存每个人的修改并合并，避免冲突。建议团队成员在编辑前先沟通，避免重复工作。</p>`,
    author: '导航页团队',
    date: '2026-07-15',
    readTime: '6分钟',
    tags: ['团队', '协作', '企业版'],
    imageSeq: 'article-team',
    imageQuery: 'Modern%20team%20collaboration%20workspace%20with%20multiple%20screens%20showing%20shared%20dashboard%2C%20warm%20office%20lighting%2C%20clean%20professional%20setting%2C%20editorial%20photography%2C%20high%20detail',
  },
  {
    slug: 'v2-0-release',
    title: 'v2.0 大版本更新：全新主题引擎和批量导入',
    category: 'updates',
    excerpt: '导航页在线发布 v2.0 正式上线！全新主题引擎、浏览器书签批量导入、访问统计升级等重磅功能。',
    content: `<h2>v2.0 更新概览</h2><p>经过三个月的研发，导航页在线发布 v2.0 版本正式上线！本次更新带来了多项重磅功能，让导航页创建更加高效和个性化。</p><h2>全新主题引擎</h2><p>我们重构了主题系统，现在支持更丰富的自定义选项：渐变背景、毛玻璃效果、自定义字体、CSS 变量配置等。同时新增了 8 套精心设计的主题模板。</p><h2>浏览器书签批量导入</h2><p>这是呼声最高的功能！现在你可以直接从浏览器导出书签文件（HTML格式），一键导入到导航页中。系统会自动识别文件夹结构并创建对应分类。</p><h2>访问统计升级</h2><p>统计面板全新改版，支持按天/周/月查看数据趋势，可以看到每个链接的点击量和点击率。数据支持导出为 CSV 文件。</p><h2>其他改进</h2><p>- 页面加载速度提升 40%<br>- 新增暗色模式支持<br>- 优化移动端编辑体验<br>- 修复若干已知问题</p>`,
    author: '导航页团队',
    date: '2026-07-10',
    readTime: '4分钟',
    tags: ['更新', 'v2.0', '新功能'],
    imageSeq: 'article-v2',
    imageQuery: 'Modern%20software%20update%20announcement%20visual%20with%20clean%20interface%20mockup%2C%20gradient%20background%2C%20tech%20aesthetic%2C%20professional%20design%2C%20editorial%20photography%20style',
  },
  {
    slug: 'privacy',
    title: '隐私政策',
    category: 'help',
    excerpt: '导航页在线发布隐私政策，了解我们如何收集、使用和保护你的个人信息。',
    content: `<h2>信息收集</h2><p>我们仅收集提供服务所必需的信息，包括：注册时的邮箱或手机号、创建的导航页内容、基本的访问日志数据。</p><h2>信息使用</h2><p>收集的信息仅用于提供和优化服务，包括：账号管理、导航页托管、访问统计分析、产品改进。</p><h2>信息保护</h2><p>我们采用业界标准的安全措施保护你的信息，包括数据加密传输（HTTPS）、访问控制、定期安全审计。</p><h2>信息共享</h2><p>我们不会将你的个人信息出售或分享给第三方，除非法律要求或获得你的明确同意。</p><h2>你的权利</h2><p>你可以随时查看、修改或删除你的个人信息和导航页内容。如需完全删除账号，请联系客服处理。</p>`,
    author: '导航页团队',
    date: '2026-06-01',
    readTime: '3分钟',
    tags: ['隐私', '政策', '法律'],
    imageSeq: 'article-privacy',
    imageQuery: 'Clean%20minimalist%20legal%20document%20concept%20with%20soft%20lighting%2C%20professional%20setting%2C%20warm%20neutral%20tones%2C%20editorial%20photography%2C%20high%20detail',
  },
  {
    slug: 'seo-optimization',
    title: '导航页 SEO 优化指南：让更多人找到你的导航页',
    category: 'tutorial',
    excerpt: '掌握导航页 SEO 优化技巧，提升搜索引擎排名，获取更多自然流量。',
    content: `<h2>SEO 为什么重要？</h2><p>优化导航页的搜索引擎排名可以帮助更多用户通过搜索找到你的资源导航，增加曝光和访问量。</p><h2>优化技巧</h2><h3>1. 设置合适的页面标题</h3><p>标题应包含核心关键词，例如"前端开发资源导航 | 你的品牌名"。长度控制在 60 字以内最佳。</p><h3>2. 添加页面描述</h3><p>在导航页设置中填写 Meta Description，简明扼要地描述导航页的内容和用途，120-160 字为佳。</p><h3>3. 绑定自定义域名</h3><p>使用自己的域名对 SEO 有显著帮助，Google 更信任独立域名的内容。</p><h3>4. 合理分类和命名</h3><p>分类名称应使用描述性词语，避免使用"其他"等无意义分类名。每个分类下的链接保持主题一致。</p><h3>5. 添加链接描述</h3><p>为每个链接添加简短描述，不仅帮助用户理解，也有助于搜索引擎理解页面内容。</p>`,
    author: '导航页团队',
    date: '2026-07-05',
    readTime: '7分钟',
    tags: ['SEO', '优化', '教程'],
    imageSeq: 'article-seo',
    imageQuery: 'SEO%20optimization%20concept%20with%20clean%20search%20engine%20analytics%20dashboard%2C%20professional%20tech%20workspace%2C%20warm%20lighting%2C%20modern%20minimalist%20design%2C%20editorial%20photography',
  },
];